import { I18n } from "@assets/strings";
import { useObservable } from "micro-observables";
import React, { useMemo } from "react";
import styled from "styled-components";
import { ApplicationDraft } from "@domain/application/applicationDraft";
import { useIsMobile } from "@utils/hooks";
import { Primary } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { StudyAccessIcon, SummaryIcon } from "@ui/components/icons";
import { Row, row, Stack } from "@ui/components/layout";
import { Page } from "@ui/components/page";
import { fromLeft, fromRight } from "@ui/styles/keyframes";
import { theme } from "@ui/styles/theme";
import { gap, mobile } from "@ui/styles/utils";
import {
	ApplicationEditionStep,
	applicationEditionSteps,
	ApplicationEditorController,
} from "@ui/pages/application/applicationEditorPage/applicationEditorController/applicationEditorController";
import { ApplicationEditorControllerContext } from "@ui/pages/application/applicationEditorPage/applicationEditorController/applicationEditorControllerContext";
import { ApplicationEditorNavigation } from "./applicationEditorNavigation";
import { DocumentSectionEditor } from "@ui/pages/application/applicationEditorPage/sections/documentSection/documentSectionEditor";
import { PersonalInformationEditor } from "@ui/pages/application/applicationEditorPage/sections/PersonalInformationSection/personalInformationSectionEditor";
import { StudyChoiceSectionEditor } from "@ui/pages/application/applicationEditorPage/sections/studyChoiceSection/studyChoiceSectionEditor";
import { VideoTeasingSectionEditor } from "@ui/pages/application/applicationEditorPage/sections/videoTeasingSection/videoTeasingSectionEditor";

import { ApplicationStatus } from "@ui/components/application/applicationStatus";
import { ApplicationStep } from "@ui/components/application/applicationStep";
import { SummarySectionView } from "@ui/components/application/summaryView";

interface ApplicationEditorProps extends React.HTMLAttributes<HTMLDivElement> {
	initialApplication: ApplicationDraft;
	applicationId?: string;
}

export const ApplicationEditor: React.FC<ApplicationEditorProps> = ({
	initialApplication,
	applicationId,
	...htmlProps
}) => {
	const controller = useMemo(() => new ApplicationEditorController(initialApplication), [initialApplication]);

	const step = useObservable(controller.step);
	const isMobile = useIsMobile();
	const isApplicationValid = useObservable(controller.isApplicationValid);
	const application = useObservable(controller.application);

	const isPersonalInformationValid = useObservable(controller.isPersonalInformationValid);
	const isStudyChoiceValid = useObservable(controller.isStudyChoiceValid);
	const isUpluoadDocumentsValid = useObservable(controller.isUpluoadDocumentsValid);
	const isVideoStoryValid = useObservable(controller.isVideoStoryValid);

	function checkStepValidity(step: ApplicationEditionStep | undefined): boolean {
		switch (step) {
			case "STUDY_CHOICES":
				return isStudyChoiceValid;
			case "PERSONAL_INFORMATIONS":
				return isPersonalInformationValid;
			case "UPLOAD_DOCUMENTS":
				return isUpluoadDocumentsValid;
			case "VIDEO_STORY":
				return isVideoStoryValid;
			default:
				return false;
		}
	}

	return (
		<ApplicationEditorControllerContext.Provider value={controller} {...htmlProps}>
			<Container>
				{!isMobile ? (
					<LeftMenuWrapper>
						{!isMobile && <Title>{I18n.t("application.space.label")}</Title>}
						<Stack gap={20} align="center">
							<LeftMenu>
								{isMobile && <Title>{I18n.t("application.space.label")}</Title>}
								<Stack gap={15}>
									{applicationEditionSteps.map((tab, index) => (
										<TabView
											key={tab}
											onClick={() => controller.setStep(tab)}
											selected={applicationEditionSteps.indexOf(step) === index}
										>
											<Label selected={applicationEditionSteps.indexOf(step) === index}>
												<b>{I18n.t(`application.step.${tab.toLocaleLowerCase()}.title`)}</b>
											</Label>
											{tab !== "SUMMARY" ? (
												<ApplicationStep isValid={checkStepValidity(tab)} />
											) : (
												<SummaryIcon
													color={applicationEditionSteps.indexOf(step) === index ? theme.colors.white : undefined}
												/>
											)}
										</TabView>
									))}
								</Stack>
							</LeftMenu>
							<Row gap={8} align="center" justify="center">
								<Primary {...htmlProps}>{I18n.t("application.status.label")} :</Primary>
								<ApplicationStatus isComplete={isApplicationValid} />
							</Row>
							<Row align="center" gap={6} justify="center">
								<Primary>{I18n.t("application.partnership.label")}</Primary>
								<StudyAccessIcon width="100" />
							</Row>
						</Stack>
					</LeftMenuWrapper>
				) : null}
				{isMobile && (
					<MobileRightSide>
						<Title>{I18n.t("application.space.label")}</Title>
						<Row style={{ alignItems: "center", justifyContent: "space-around" }}>
							{applicationEditionSteps.map((tab, index) => (
								<React.Fragment key={tab}>
									<MobileTabView onClick={() => controller.setStep(tab)}>
										<ApplicationStep
											isMobile={isMobile}
											isValid={checkStepValidity(tab)}
											isCurrentStep={applicationEditionSteps.indexOf(step) === index}
										/>
									</MobileTabView>
									{index !== applicationEditionSteps.length - 1 && <Wire key={`mobile_${tab}`} />}
								</React.Fragment>
							))}
						</Row>
					</MobileRightSide>
				)}
				{step === "STUDY_CHOICES" ? (
					<ContentBlock>
						<Title>{I18n.t("application.step.study_choices.title")}</Title>
						<RightBloc>
							<StepBanner padding={20} linearColor={theme.colors.primaryGradient}>
								{I18n.t("application.step.study_choices.description")}
							</StepBanner>
							<StudyChoiceSectionEditor />
						</RightBloc>
					</ContentBlock>
				) : step === "PERSONAL_INFORMATIONS" ? (
					<ContentBlock>
						<Title>{I18n.t("application.step.personal_informations.title")}</Title>
						<RightBloc>
							<StepBanner padding={20} linearColor={theme.colors.secondaryGradient}>
								{I18n.t("application.step.personal_informations.description")}
							</StepBanner>
							<PersonalInformationEditor />
						</RightBloc>
					</ContentBlock>
				) : step === "UPLOAD_DOCUMENTS" ? (
					<ContentBlock>
						<Title>{I18n.t("application.step.upload_documents.title")}</Title>
						<RightBloc>
							<StepBanner padding={20} linearColor={theme.colors.thirdGradient}>
								{I18n.t("application.step.upload_documents.description")}
							</StepBanner>
							<DocumentSectionEditor />
						</RightBloc>
					</ContentBlock>
				) : step === "VIDEO_STORY" ? (
					<ContentBlock>
						<Title>{I18n.t("application.step.video_story.title")}</Title>
						<RightBloc>
							<StepBanner padding={20} linearColor={theme.colors.fourthGradient}>
								{I18n.t("application.step.video_story.description")}
							</StepBanner>
							<VideoTeasingSectionEditor />
						</RightBloc>
					</ContentBlock>
				) : (
					<ContentBlock>
						<Title>{I18n.t("application.step.summary.title")}</Title>
						<RightBloc>
							<SummarySectionView
								application={application}
								renderIcons={(step) => <ApplicationStep isValid={checkStepValidity(step)} />}
							/>
						</RightBloc>
					</ContentBlock>
				)}
				<BlockFooter>
					<StyleEditorNavigation applicationId={applicationId} />
				</BlockFooter>
			</Container>
		</ApplicationEditorControllerContext.Provider>
	);
};

const Container = styled(Page)`
	width: 100%;
	margin-bottom: 40px;
	display: grid;
	grid-template-columns: 280px minmax(0, 1fr);
	grid-template-rows: auto;
	grid-column-gap: 45px;
	grid-row-gap: 24px;
	grid-template-areas:
		"menu content"
		". content"
		". content"
		". content"
		". footer";

	@media ${mobile} {
		display: block;
	}
`;

const LeftMenuWrapper = styled.div`
	grid-area: menu;
	position: sticky;
	width: 100%;
	${gap(20)};
	@media ${mobile} {
		${gap(30)};
		animation: ${fromLeft} 0.2s linear;
	}
`;

const StyleEditorNavigation = styled(ApplicationEditorNavigation)``;

const LeftMenu = styled(RoundedBlock)`
	padding: 20px 10px 20px 10px;
	${gap(38)};
	width: 100%;
`;

const Title = styled.div`
	${row(16, "flex-start", "baseline")};
	${({ theme }) => theme.textStyles.subtitle};
`;

const TabView = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>`
	cursor: pointer;
	background: ${({ selected }) =>
		selected ? "linear-gradient(90deg, #206AF8 6.16%, rgba(32, 106, 248, 0.76) 132.57%)" : "#f9fbff"};
	border-radius: 10px;
	padding: 15px 10px;
	width: 100%;
	${row(0, "space-between", "center")};
	will-change: transform;
	svg  {
		transition: transform ease-in-out 0.2s;
		transform: rotate(-90deg);
	}
	&:hover svg {
		transform: rotate(-90deg) translateY(2px);
	}
`;

const MobileTabView = styled.div`
	cursor: pointer;
	> svg {
		width: 20px;
		height: 20px;
	}
`;

const Label = styled(Primary)<{ selected?: boolean }>`
	color: ${({ theme, selected }) => (selected ? theme.colors.white : "#525252")};
`;

const RightBloc = styled(RoundedBlock)`
	position: relative;
	padding: 30px 31px 40px 36px;
`;

const StepBanner = styled.div.withConfig({
	shouldForwardProp: (prop) => !["linearColor", "padding"].includes(prop),
})<{ linearColor: string; padding?: number }>`
	background: ${({ linearColor }) => linearColor && `linear-gradient(${linearColor})`};
	padding: ${({ padding }) => padding && `${padding}px`};
	position: absolute;
	top: 0px;
	left: 0px;
	width: 100%;
	border-radius: 13px;
	color: ${theme.colors.white};
`;

const ContentBlock = styled.div`
	grid-area: content;
	width: 100%;
	${gap(20)};
	@media ${mobile} {
		padding: 22px 0px;
	}
`;

const BlockFooter = styled.div`
	grid-area: footer;
	${gap(20)};
	@media ${mobile} {
		padding: 0px;
	}
`;

const MobileRightSide = styled.div`
	${gap(30)};
	animation: ${fromRight} 0.2s linear;
`;

const Wire = styled.div`
	height: 2px;
	width: 108px;
	background-color: ${theme.colors.lightergray};
`;

// const StyledSaaIcons = styled()`
// 	margin: 110px;
// `;
